import { useMemo, useCallback } from 'react';
import Fuse from 'fuse.js';
import { flattenNavItems, getSearchableItems, FlattenedNavItem } from '../utils/flattenNavItems';
import { useAuth } from './useAuth';
import { checkRoutePermission } from '../utils/permissionUtils';
import { Role } from '../constants/roles';

type SearchResult = {
  item: FlattenedNavItem;
  score?: number;
};

/**
 * Hook for searching through sidebar navigation items using Fuse.js
 * Provides fuzzy search capabilities with permission filtering
 */
export function useSidebarSearch() {
  const authContext = useAuth();
  
  // Memoize the flattened and filtered items based on permissions
  const searchableItems = useMemo(() => {
    try {
      // Safe destructuring with fallbacks inside useMemo
      const user = authContext?.user || null;
      const navigation = authContext?.navigation || [];
      const permissions = authContext?.permissions || [];
      const userRoles = user?.roles || [];
      
      // console.log('[useSidebarSearch] Raw navigation from auth context:', navigation);
      // console.log('[useSidebarSearch] User permissions:', permissions);
      // console.log('[useSidebarSearch] User roles:', userRoles);
      
      if (!navigation || navigation.length === 0) {
        // console.log('[useSidebarSearch] No navigation data available');
        return [];
      }
      
      const flattened = flattenNavItems(navigation);
      // console.log('[useSidebarSearch] Flattened items:', flattened);
      
      const searchable = getSearchableItems(flattened);
      // console.log('[useSidebarSearch] Searchable items:', searchable);
      
      // Filter items based on user permissions
      const filtered = searchable.filter(item => {
        // Try different approaches to check permissions:
        // 1. Use urlPath/path to check route permissions
        // 2. Use code if it matches permission pattern
        // 3. Default to allow if no specific restrictions
        
        let hasPermission = false;
        
        // Check by URL path using route permission checking
        if (item.urlPath) {
          hasPermission = checkRoutePermission(item.urlPath, userRoles as Role[], permissions);
          // console.log(`[useSidebarSearch] Item ${item.name} (${item.code}) - URL: ${item.urlPath} - Route permission: ${hasPermission}`);
        }
        
        // If route permission failed, try checking if the code matches a permission pattern
        if (!hasPermission && item.code && permissions) {
          // Try direct code match
          const directMatch = permissions.includes(item.code);
          // console.log(`[useSidebarSearch] Item ${item.name} (${item.code}) - Direct code match: ${directMatch}`);
          
          // Try code as permission pattern (e.g., if code is "dashboard", check for "dashboard.view")
          const codeAsPermission = permissions.some(perm => 
            perm.startsWith(item.code) || 
            perm.startsWith(item.code + '.') ||
            item.code.includes(perm)
          );
          // console.log(`[useSidebarSearch] Item ${item.name} (${item.code}) - Code as permission: ${codeAsPermission}`);
          
          hasPermission = directMatch || codeAsPermission;
        }
        
        // If still no permission, default to true for now (to show all items while debugging)
        if (!hasPermission) {
          // console.log(`[useSidebarSearch] Item ${item.name} (${item.code}) - Defaulting to ALLOW for debugging`);
          hasPermission = true; // Temporary: allow all items to see what should be filtered
        }
        
        // // console.log(`[useSidebarSearch] Item ${item.name} (${item.code}) - Final permission: ${hasPermission}`);
        return hasPermission;
      });
      
      // console.log('[useSidebarSearch] Final filtered items:', filtered);
      return filtered;
    } catch (error) {
      console.error('Error processing navigation items:', error);
      return [];
    }
  }, [authContext]);

  // Initialize Fuse.js with optimized configuration
  const fuse = useMemo(() => {
    return new Fuse(searchableItems, {
      keys: [
        { name: 'name', weight: 0.7 },
        { name: 'fullPath', weight: 0.3 }
      ],
      includeScore: true,
      threshold: 0.3,          // Lower = more strict matching
      distance: 100,           // Maximum character distance
      minMatchCharLength: 1,   // Minimum character length to trigger search
      ignoreLocation: true,    // Ignore position of match in string
      shouldSort: true,        // Sort results by score
    });
  }, [searchableItems]);

  /**
   * Performs fuzzy search on navigation items
   * @param query - Search query string
   * @returns Array of search results with scores
   */
  const search = useCallback((query: string): SearchResult[] => {
    try {
      if (!query.trim()) {
        return searchableItems.map(item => ({ item }));
      }

      const results = fuse.search(query);
      return results.map(result => ({
        item: result.item,
        score: result.score
      }));
    } catch (error) {
      console.error('Error performing search:', error);
      return [];
    }
  }, [fuse, searchableItems]);

  /**
   * Gets all searchable items without filtering
   * @returns All navigation items the user has permission to view
   */
  const getAllItems = useCallback((): SearchResult[] => {
    try {
      return searchableItems.map(item => ({ item }));
    } catch (error) {
      console.error('Error getting all items:', error);
      return [];
    }
  }, [searchableItems]);

  /**
   * Gets items grouped by their parent category
   * @returns Object with categories as keys and items as values
   */
  const getGroupedItems = useCallback(() => {
    try {
      const grouped: Record<string, FlattenedNavItem[]> = {};
      
      searchableItems.forEach(item => {
        const parts = item.fullPath.split(' > ');
        const category = parts[0] || 'Other';
        
        if (!grouped[category]) {
          grouped[category] = [];
        }
        grouped[category].push(item);
      });
      
      return grouped;
    } catch (error) {
      console.error('Error grouping items:', error);
      return {};
    }
  }, [searchableItems]);

  return {
    search,
    getAllItems,
    getGroupedItems,
    totalItems: searchableItems.length
  };
}